import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormReducer } from '@tphglobal/common/hooks';
import {
  Button,
  Form,
  FormError,
  FormRow,
  FormRowItem,
  OutlinedContainer,
  TextInput,
} from '@tphglobal/components';
import { brand, greyScaleColour } from '@tphglobal/common/theme/style.palette';
import { HttpMethods } from '@tphglobal/common/utils';
import useWindowSize from '@tphglobal/common/hooks/windowSize';
import { breakpoints } from '@tphglobal/common/theme/style.layout';
import { RegionTypes } from '@tphglobal/common/utils/constants';
import handleApiCall from '../handleApiCall';
import { ReduxState } from '../../redux/reducers';
import messages from '../../messages';
import { emptyValueValidator, required } from '../../utils';
import { CONTACT_US_AU, CONTACT_US_UK } from '../../api';

interface Props {
  onCancel: () => void;
  onSuccess: () => void;
}

const validators = {
  message: [required(messages?.general?.validations?.messageRequired), emptyValueValidator],
};

const ContactUs: React.FC<Props> = ({ onCancel, onSuccess }) => {
  const reduxDispatch = useDispatch();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const selectedRegion = useSelector(
    (state: ReduxState) => state?.profile?.region,
  );
  const {
    submitError, handleSubmit, connectField, setSubmitError,
  } = useFormReducer(validators);
  const profile = useSelector((state: ReduxState) => state?.profile);

  interface SubmitData {
    message : string
  }
  const onSubmit = async (data: SubmitData) => {
    const sanitizedBody = {
      from: profile?.EmailAddress?.trim(),
      name: `${profile?.FirstName} ${profile?.LastName}`?.trim(),
      subject: "Feedback Freelance Portal",
      Preview: false,
      message: `Name: ${profile?.FirstName} ${profile?.LastName}\nEmail: ${profile?.EmailAddress?.trim()} \nMessage: ${data?.message?.trim()}`,
    };
    setSubmitting(true);
    handleApiCall(
      reduxDispatch,
      selectedRegion === RegionTypes.AU ? CONTACT_US_AU : CONTACT_US_UK,
      () => {
        setSubmitting(false);
        onSuccess();
      },
      HttpMethods.POST,
      selectedRegion === RegionTypes.AU ? RegionTypes.AU : RegionTypes.UK,
      messages?.support?.contactUsForm?.success,
      sanitizedBody,
    );
  };
  const { width } = useWindowSize();

  const isMobile = width <= breakpoints.mobile;

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Grid padding={isMobile ? '0 16px' : '0 24px'}>
        <FormRow marginBottom={0}>
          {connectField('message', {
            label: messages?.support?.contactUsForm?.message,
            multiline: true,
            rows: 3,
            autoHeight: true,
          })(TextInput)}
        </FormRow>
        {submitError && (
          <FormRow>
            <FormRowItem>
              <FormError
                message={messages?.departments?.addForm?.failure?.[submitError]}
              />
            </FormRowItem>
          </FormRow>
        )}
      </Grid>

      <OutlinedContainer
        noBorder
        borderRadius="0"
        padding="14px 24px"
        styles={{ backgroundColor: greyScaleColour?.grey60, marginTop: '24px' }}
      >
        <Grid container justifyContent="flex-end">
          <Grid item>
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  variant="outlined"
                  buttonColor={brand?.black}
                  color="primary"
                  onClick={onCancel}
                  size="large"
                  label={messages?.general?.cancel}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  type="submit"
                  disabled={submitting}
                  label={messages?.general?.sendMsg}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </OutlinedContainer>
    </Form>
  );
};

export default ContactUs;
